import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import type {
  DBEquipmentBooking,
  DBEquipmentBookingConflict,
  DBEquipmentBookingLine,
  DBEquipmentUsage,
} from "@/lib/db-types";

/** Kolonnene i equipment_booking_lines (VIB-2919). */
export const BOOKING_LINE_COLUMNS =
  "id, booking_id, item_id, quantity, date_from, date_to, note, project_id, title, responsible_user_id, created_by, project_code, project_name, responsible_name, item_name, item_quantity, item_status, item_department_id, item_department_name";

const CONFLICT_COLUMNS =
  "booking_item_id, booking_id, item_id, item_name, other_booking_item_id, other_booking_id, other_project_id, other_project_code, other_project_name, other_title, other_quantity, other_date_from, other_date_to, other_responsible_name";

const sortLines = (rows: DBEquipmentBookingLine[]) =>
  rows.sort((a, b) => a.date_from.localeCompare(b.date_from) || a.item_name.localeCompare(b.item_name, "nb"));

/**
 * Alle aktive bookinglinjer som slutter `from` eller senere, for hele teamet.
 * Brukes til status i utstyrslista og konfliktvarsel i bookingdialogen.
 */
export function useEquipmentBookingLines(from: string, enabled = true) {
  return useQuery({
    queryKey: ["equipment-bookings", { from }],
    enabled,
    queryFn: () =>
      fetchAllPaginated<DBEquipmentBookingLine>("equipment_booking_lines", BOOKING_LINE_COLUMNS, (q) => q.gte("date_to", from)),
  });
}

/** Aktive bookinglinjer som overlapper vinduet, for hele teamet (belegget, VIB-2920). */
export function useEquipmentOccupancy(range: { from: string; to: string }) {
  return useQuery({
    queryKey: ["equipment-bookings", range],
    queryFn: () =>
      fetchAllPaginated<DBEquipmentBookingLine>("equipment_booking_lines", BOOKING_LINE_COLUMNS, (q) =>
        q.gte("date_to", range.from).lte("date_from", range.to),
      ),
  });
}

/** Bookinglinjene på ett prosjekt (fanen «Utstyr»). */
export function useProjectEquipment(projectId: string | undefined) {
  return useQuery({
    queryKey: ["project-equipment", projectId],
    enabled: !!projectId,
    queryFn: async (): Promise<DBEquipmentBookingLine[]> => {
      const { data, error } = await supabase
        .from("equipment_booking_lines")
        .select(BOOKING_LINE_COLUMNS)
        .eq("project_id", projectId!);
      if (error) throw error;
      return sortLines((data ?? []) as DBEquipmentBookingLine[]);
    },
  });
}

/** Dobbelbookinger for linjene på et prosjekt, med det andre prosjektet. */
export function useProjectEquipmentConflicts(projectId: string | undefined, lineIds: string[]) {
  return useQuery({
    queryKey: ["project-equipment-conflicts", projectId, lineIds],
    enabled: !!projectId && lineIds.length > 0,
    queryFn: async (): Promise<DBEquipmentBookingConflict[]> => {
      const { data, error } = await supabase
        .from("equipment_booking_conflicts")
        .select(CONFLICT_COLUMNS)
        .in("booking_item_id", lineIds);
      if (error) throw error;
      return (data ?? []) as DBEquipmentBookingConflict[];
    },
  });
}

/** Bookingene på ett utstyr (kalenderen på utstyrskortet). */
export function useEquipmentItemBookings(equipmentId: string | undefined) {
  return useQuery({
    queryKey: ["equipment-item-bookings", equipmentId],
    enabled: !!equipmentId,
    queryFn: async (): Promise<DBEquipmentBookingLine[]> => {
      const { data, error } = await supabase
        .from("equipment_booking_lines")
        .select(BOOKING_LINE_COLUMNS)
        .eq("item_id", equipmentId!);
      if (error) throw error;
      return sortLines((data ?? []) as DBEquipmentBookingLine[]);
    },
  });
}

/** «Brukt 12 mnd» per utstyr. */
export function useEquipmentUsage(enabled = true) {
  return useQuery({
    queryKey: ["equipment-usage"],
    enabled,
    queryFn: async (): Promise<Map<string, DBEquipmentUsage>> => {
      const rows = await fetchAllPaginated<DBEquipmentUsage>("equipment_usage_12m", "item_id, booked_days, bookings", undefined, "item_id");
      return new Map(rows.map((r) => [r.item_id, r]));
    },
    staleTime: 5 * 60 * 1000,
  });
}

export interface EquipmentBookingDetail extends DBEquipmentBooking {
  lines: DBEquipmentBookingLine[];
}

/** Én booking med linjene, for å åpne den i dialogen. */
export function useEquipmentBooking(bookingId: string | null) {
  return useQuery({
    queryKey: ["equipment-booking", bookingId],
    enabled: !!bookingId,
    queryFn: async (): Promise<EquipmentBookingDetail | null> => {
      const [booking, lines] = await Promise.all([
        supabase
          .from("equipment_bookings")
          .select("id, project_id, title, responsible_user_id, status, created_by")
          .eq("id", bookingId!)
          .maybeSingle(),
        supabase.from("equipment_booking_lines").select(BOOKING_LINE_COLUMNS).eq("booking_id", bookingId!),
      ]);
      if (booking.error) throw booking.error;
      if (lines.error) throw lines.error;
      if (!booking.data) return null;
      return { ...(booking.data as DBEquipmentBooking), lines: sortLines((lines.data ?? []) as DBEquipmentBookingLine[]) };
    },
  });
}
